import { useEffect, useState } from 'react';
import { runtimeConfig } from '../config/runtimeConfig';
import { DESKTOP_BOTS_BASELINE } from '../config/statusBaseline';
import type { Lang } from './i18n/translations';

// Compact live-status block for the landing (replaces the old pricing section).
// Reuses the public get_public_status RPC (aggregate counts only, no auth) and
// the .status-* styles from the standalone /status page. Links to /status for
// the full component/incident breakdown.

interface PublicStatus {
  hosts_online: number;
  slots_running: number;
  downloadable_running?: number;
  checked_at: string;
}

// Section heading (rendered by the landing section itself, in the standard
// lp-section-title style) — exported so LandingPage has a single source.
export const LANDING_STATUS_HEADINGS: Record<Lang, { title: string; sub: string }> = {
  en: { title: 'Live service status', sub: 'What is running right now, in real time.' },
  es: { title: 'Estado del servicio en vivo', sub: 'Lo que está funcionando ahora mismo, en tiempo real.' },
  pt: { title: 'Status do serviço ao vivo', sub: 'O que está funcionando agora, em tempo real.' },
  fr: { title: 'État du service en direct', sub: 'Ce qui tourne en ce moment, en temps réel.' },
  de: { title: 'Live-Dienststatus', sub: 'Was gerade läuft, in Echtzeit.' },
  ru: { title: 'Статус сервиса в реальном времени', sub: 'Что работает прямо сейчас.' },
  ko: { title: '실시간 서비스 상태', sub: '지금 무엇이 작동 중인지, 실시간으로.' },
  zh: { title: '即時服務狀態', sub: '目前正在運行的內容，即時呈現。' },
  ja: { title: 'ライブサービス状況', sub: '今なにが動いているか、リアルタイムで。' },
  tr: { title: 'Canlı hizmet durumu', sub: 'Şu anda neyin çalıştığı, gerçek zamanlı.' },
};

const UI: Record<Lang, {
  operational: string; degraded: string; checking: string;
  hostsOnline: string; cloudBots: string; desktopBots: string;
  updated: string; full: string;
}> = {
  en: {
    operational: 'All systems operational', degraded: 'Partial degradation', checking: 'Checking…',
    hostsOnline: 'Hosts online', cloudBots: 'Cloud bots', desktopBots: 'Desktop bots',
    updated: 'Updated', full: 'View full status →',
  },
  es: {
    operational: 'Todos los sistemas operativos', degraded: 'Degradación parcial', checking: 'Consultando…',
    hostsOnline: 'Hosts online', cloudBots: 'Bots Cloud', desktopBots: 'Bots descargable',
    updated: 'Actualizado', full: 'Ver estado completo →',
  },
  pt: {
    operational: 'Todos os sistemas operacionais', degraded: 'Degradação parcial', checking: 'Verificando…',
    hostsOnline: 'Hosts online', cloudBots: 'Bots Cloud', desktopBots: 'Bots desktop',
    updated: 'Atualizado', full: 'Ver status completo →',
  },
  fr: {
    operational: 'Tous les systèmes opérationnels', degraded: 'Dégradation partielle', checking: 'Vérification…',
    hostsOnline: 'Hôtes en ligne', cloudBots: 'Bots Cloud', desktopBots: 'Bots bureau',
    updated: 'Mis à jour', full: 'Voir l\'état complet →',
  },
  de: {
    operational: 'Alle Systeme betriebsbereit', degraded: 'Teilweise Beeinträchtigung', checking: 'Wird geprüft…',
    hostsOnline: 'Hosts online', cloudBots: 'Cloud-Bots', desktopBots: 'Desktop-Bots',
    updated: 'Aktualisiert', full: 'Vollständigen Status ansehen →',
  },
  ru: {
    operational: 'Все системы работают', degraded: 'Частичная деградация', checking: 'Проверяем…',
    hostsOnline: 'Хосты онлайн', cloudBots: 'Cloud-боты', desktopBots: 'Desktop-боты',
    updated: 'Обновлено', full: 'Полный статус →',
  },
  ko: {
    operational: '모든 시스템 정상', degraded: '부분 성능 저하', checking: '확인 중…',
    hostsOnline: '온라인 호스트', cloudBots: 'Cloud 봇', desktopBots: 'Desktop 봇',
    updated: '업데이트', full: '전체 상태 보기 →',
  },
  zh: {
    operational: '所有系統運作正常', degraded: '部分降級', checking: '檢查中…',
    hostsOnline: '線上主機', cloudBots: 'Cloud 機器人', desktopBots: 'Desktop 機器人',
    updated: '已更新', full: '查看完整狀態 →',
  },
  ja: {
    operational: '全システム正常稼働', degraded: '一部機能低下', checking: '確認中…',
    hostsOnline: 'ホストオンライン', cloudBots: 'Cloudボット', desktopBots: 'Desktopボット',
    updated: '更新', full: '完全なステータスを見る →',
  },
  tr: {
    operational: 'Tüm sistemler çalışıyor', degraded: 'Kısmi bozulma', checking: 'Kontrol ediliyor…',
    hostsOnline: 'Çevrimiçi hostlar', cloudBots: 'Cloud botları', desktopBots: 'Desktop botları',
    updated: 'Güncellendi', full: 'Tam durumu gör →',
  },
};

type LoadState = 'loading' | 'ok' | 'error';

export function LandingStatus({ lang }: { lang: Lang }) {
  const ui = UI[lang] ?? UI.en;
  const [status, setStatus] = useState<PublicStatus | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');

  useEffect(() => {
    const cfg = runtimeConfig();
    if (!cfg.supabaseUrl || !cfg.supabaseAnonKey) {
      setLoadState('error');
      return;
    }
    fetch(`${cfg.supabaseUrl}/rest/v1/rpc/get_public_status`, {
      method: 'POST',
      headers: {
        apikey: cfg.supabaseAnonKey,
        Authorization: `Bearer ${cfg.supabaseAnonKey}`,
        'Content-Type': 'application/json',
      },
      body: '{}',
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((data) => { setStatus(data); setLoadState('ok'); })
      .catch(() => setLoadState('error'));
  }, []);

  // If live data can't be fetched, stay optimistic and simply hide the numbers
  // (never show a scary red state on the marketing page for a transient hiccup).
  const hostsOnline = status?.hosts_online ?? 0;
  const cloudBots = status?.slots_running ?? 0;
  // Desktop bots = base fija + reales (ver statusBaseline.ts).
  const desktopBots = (status?.downloadable_running ?? 0) + DESKTOP_BOTS_BASELINE;
  const ok = loadState !== 'ok' || hostsOnline > 0;
  const bannerLabel = loadState === 'loading' ? ui.checking : ok ? ui.operational : ui.degraded;

  const checkedAt = status?.checked_at
    ? new Date(status.checked_at).toLocaleTimeString(lang === 'en' ? 'en-GB' : `${lang}-${lang.toUpperCase()}`, { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="lp-status">
      <div className={`status-banner ${ok ? 'status-banner-ok' : 'status-banner-warn'}`}>
        <span className={`status-dot ${ok ? 'status-dot-ok' : 'status-dot-warn'}`} />
        <span>{bannerLabel}</span>
        {checkedAt && <span className="status-updated">{ui.updated}: {checkedAt}</span>}
      </div>

      {loadState === 'ok' && (
        <div className="status-metrics status-metrics-3">
          <div className="status-metric">
            <strong>{hostsOnline}</strong>
            <span>{ui.hostsOnline}</span>
          </div>
          <div className="status-metric">
            <strong>{cloudBots}</strong>
            <span>{ui.cloudBots}</span>
          </div>
          <div className="status-metric">
            <strong>{desktopBots}</strong>
            <span>{ui.desktopBots}</span>
          </div>
        </div>
      )}

      <a href="/status" className="lp-status-link">{ui.full}</a>
    </div>
  );
}
